// Parent homework: the entry point and the day-box helpers.
// renderHomeworkParent mounts the Today / All homework surface (parent/surface.js) into one cached persistent host, so re()'s
// root.replaceChildren re-inserts the live subtree instead of tearing it down (the surface owns its own refresh via refreshHomeworkParent).
// The helpers below are shared: the specialist detail view (detail-view.js) and the parent surface (parent/all-homework.js, hw-board.js, today.js).
// The old multi-screen flow (bubble list, exercise list, exercise detail, status picker, completion details) was unreachable since the
// surface switch (SC-H1a) and was removed in the cleanup of review 237.

import { exerciseSlotsOn } from './data.js';
import { injectHomeworkStyles } from './styles.js';

const _pad = n => String(n).padStart(2, '0');
const _fmtLocal = d => d.getFullYear() + '-' + _pad(d.getMonth() + 1) + '-' + _pad(d.getDate());

// SC-H1a: cached persistent host for the Today/All surface. renderHomeworkParent returns this same node on every re-entry.
let _hwpCachedHost = null;

export function renderHomeworkParent({ childId, isWeb, initialExercise }) {
  injectHomeworkStyles();
  if (!_hwpCachedHost) _hwpCachedHost = document.createElement('div');
  try { window.HUD_HOMEWORK.mountHomeworkParent(_hwpCachedHost, { childId, isWeb, initialExercise }); }
  catch (e) { console.error('❌ mountHomeworkParent:', e); }
  return _hwpCachedHost;
}


// Active/Past classification (Sub-commit 6).
// 'past' rules per design doc §8.3 + Q1:
//   - duration_type='end_date' AND end_date < today → past
//   - duration_type='next_appointment' AND no upcoming appointment AND a past appointment exists for the (child, specialist) pair → past
//   - duration_type='open_ended' → never auto-past (specialist must archive manually)
//   - 'next_appointment' with no appointments at all → active (no end declared)
export function _isHomeworkPast(hw, today, H) {
  const todayStr = _fmtLocal(today);

  if (hw.duration_type === 'end_date' && hw.end_date) {
    return hw.end_date < todayStr;
  }

  if (hw.duration_type === 'next_appointment') {
    // If an upcoming appointment exists, the homework is still bounded ahead → active
    const nextApptDate = _findNextAppointment(hw.child_id, hw.specialist_id, H);
    if (nextApptDate) return false;
    // No upcoming → check whether there was a past appointment for this (child, specialist) pair
    const apts = H.DB?.appointments || [];
    return apts.some(a =>
      a.childId === hw.child_id &&
      (a.specialistId === hw.specialist_id || (a.sharedWith || []).includes(hw.specialist_id)) &&
      !a.deletedAt &&
      a.date && a.date < todayStr
    );
  }

  // 'open_ended' or any unrecognized duration_type → never auto-past
  return false;
}

// Strict 'allDone' semantic — day shows ✓ only when every scheduled slot has status='done'.
export function _isDayDone(hw, date, compMap) {
  const dStr = _fmtLocal(date);
  let total = 0, done = 0;
  (hw.exercises || []).forEach(ex => {
    const slots = exerciseSlotsOn(hw, ex, date);
    slots.forEach(slot => {
      total++;
      if (compMap[ex.id + ':' + dStr + ':' + slot]?.status === 'done') done++;
    });
  });
  return total > 0 && done === total;
}

// Find next non-deleted appointment for this child + specialist (or null).
export function _findNextAppointment(childId, specId, H) {
  const apts = H.DB?.appointments || [];
  const todayStr = _fmtLocal(new Date());
  const matching = apts
    .filter(a => a.childId === childId
              && (a.specialistId === specId || (a.sharedWith || []).includes(specId))
              && !a.deletedAt
              && a.date && a.date >= todayStr)
    .sort((a, b) => a.date.localeCompare(b.date));
  return matching[0]?.date || null;
}

// Generate the visible day-box window per duration_type rules + recurrence schedule.
// Returns array of { date, dStr, isToday, isPast, isFuture, isAssigned } — only days
// where the homework is actually scheduled. Empty array means the bubble should hide.
export function _generateDayBoxWindow(hw, today, nextApptDate) {
  // Once-off: always show its single day, regardless of window position (Q2 locked).
  if (hw.recurrence === 'once') {
    if (!hw.created_at) return [];
    const d = new Date(hw.created_at); d.setHours(0, 0, 0, 0);
    const dStr = _fmtLocal(d);
    return [{
      date: d,
      dStr,
      isToday: dStr === _fmtLocal(today),
      isPast: d < today,
      isFuture: d > today,
      isAssigned: true,
    }];
  }

  if (!hw.created_at) return [];
  const startDate = new Date(hw.created_at); startDate.setHours(0, 0, 0, 0);

  const SLIDING_BACK = 14;  // days
  const SLIDING_FWD = 7;    // days
  const dayMs = 86400000;
  const todayMs = today.getTime();
  const startMs = startDate.getTime();

  let windowStart, windowEnd;
  if (hw.duration_type === 'end_date' && hw.end_date) {
    const endDate = new Date(hw.end_date + 'T00:00:00'); endDate.setHours(0, 0, 0, 0);
    const periodDays = Math.round((endDate.getTime() - startMs) / dayMs);
    if (periodDays > SLIDING_BACK) {
      // Long period → sliding window, clamped to [startDate, endDate]
      windowStart = new Date(Math.max(startMs, todayMs - SLIDING_BACK * dayMs));
      windowEnd = new Date(Math.min(endDate.getTime(), todayMs + SLIDING_FWD * dayMs));
    } else {
      // Short period → show the full bounded period
      windowStart = startDate;
      windowEnd = endDate;
    }
  } else if (hw.duration_type === 'next_appointment') {
    if (nextApptDate) {
      const apptDate = new Date(nextApptDate + 'T00:00:00'); apptDate.setHours(0, 0, 0, 0);
      windowStart = new Date(Math.max(startMs, todayMs - SLIDING_BACK * dayMs));
      windowEnd = apptDate;
    } else {
      // No upcoming appointment → fall back to sliding window
      windowStart = new Date(Math.max(startMs, todayMs - SLIDING_BACK * dayMs));
      windowEnd = new Date(todayMs + SLIDING_FWD * dayMs);
    }
  } else {
    // 'open_ended' or unrecognized → sliding window
    windowStart = new Date(Math.max(startMs, todayMs - SLIDING_BACK * dayMs));
    windowEnd = new Date(todayMs + SLIDING_FWD * dayMs);
  }
  windowStart.setHours(0, 0, 0, 0);
  windowEnd.setHours(0, 0, 0, 0);

  if (windowEnd < windowStart) return [];

  const todayStr = _fmtLocal(today);
  const out = [];
  const cursor = new Date(windowStart);
  while (cursor <= windowEnd) {
    const isAssigned = (hw.exercises || []).some(ex => isExerciseScheduledOn(hw, ex, cursor));
    if (isAssigned) {
      const dCopy = new Date(cursor);
      const dStr = _fmtLocal(dCopy);
      out.push({
        date: dCopy,
        dStr,
        isToday: dStr === todayStr,
        isPast: dCopy < today,
        isFuture: dCopy > today,
        isAssigned: true,
      });
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
}

// Scroll behaviors for any day-box row: auto-center on today, mouse wheel translation,
// click-and-drag scroll. Extracted so detail-view (specialist side) can reuse without
// duplicating the ~80 lines of behavior glue. Pure DOM behavior — no parent-state deps.
export function _attachDayBoxRowScroll(row) {
  // Center today's box in the visible scroll viewport on initial render.
  // Uses getBoundingClientRect rather than offsetLeft because the row's offsetParent
  // may be <body> (no positioned ancestor in the chain), so offsetLeft returns absolute
  // page-x coordinates and over-shoots the scroll on long-history homeworks.
  // Double-rAF gives flex layout an extra frame to settle before measuring.
  // No-op when content fits without scrolling.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      try {
        const todayBox = row.querySelector('[data-today="1"]');
        if (!todayBox) return;
        if (row.scrollWidth <= row.clientWidth) return;
        const todayRect = todayBox.getBoundingClientRect();
        const rowRect = row.getBoundingClientRect();
        const todayLeftInRow = todayRect.left - rowRect.left + row.scrollLeft;
        const target = todayLeftInRow + (todayRect.width / 2) - (row.clientWidth / 2);
        row.scrollLeft = Math.max(0, target);
      } catch (_) {}
    });
  });

  // Translate vertical mouse wheel into horizontal scroll on desktop.
  row.addEventListener('wheel', (e) => {
    if (e.deltaX !== 0) return;
    if (e.deltaY === 0) return;
    if (row.scrollWidth <= row.clientWidth) return;
    const max = row.scrollWidth - row.clientWidth;
    const atLeft = row.scrollLeft <= 0;
    const atRight = row.scrollLeft >= max;
    if ((e.deltaY < 0 && atLeft) || (e.deltaY > 0 && atRight)) return;
    e.preventDefault();
    row.scrollLeft = Math.max(0, Math.min(max, row.scrollLeft + e.deltaY));
  }, { passive: false });

  // Click-and-drag scroll for desktop. 5px threshold distinguishes drag from click.
  let isDragging = false;
  let dragStartX = 0;
  let dragStartScrollLeft = 0;
  let totalDragDistance = 0;
  const DRAG_THRESHOLD_PX = 5;

  row.style.cursor = 'grab';
  row.style.userSelect = 'none';

  row.addEventListener('mousedown', (e) => {
    if (e.button !== 0) return;
    isDragging = true;
    dragStartX = e.pageX;
    dragStartScrollLeft = row.scrollLeft;
    totalDragDistance = 0;
    row.style.cursor = 'grabbing';
  });

  row.addEventListener('mousemove', (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const dx = e.pageX - dragStartX;
    totalDragDistance = Math.abs(dx);
    row.scrollLeft = dragStartScrollLeft - dx;
  });

  const endDrag = () => {
    if (!isDragging) return;
    isDragging = false;
    row.style.cursor = 'grab';
  };

  row.addEventListener('mouseup', (e) => {
    if (totalDragDistance > DRAG_THRESHOLD_PX) {
      e.preventDefault();
      e.stopPropagation();
    }
    endDrag();
  });

  row.addEventListener('mouseleave', endDrag);
}
